import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Compass, Users } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { riseFade } from "@/lib/motion";
import PillarSeo from "@/components/seo/PillarSeo";
import RelatedPillars from "@/components/seo/RelatedPillars";
import ShareButtons from "@/components/blog/ShareButtons";
import BookmarkButton from "@/components/blog/BookmarkButton";
import RelatedArticles from "@/components/blog/RelatedArticles";

const META_TITLE = "Living With MS: Plan Your Day Around Fatigue & Brain Fog | Myelina Health";
const META_DESC =
  "Living with MS is a sequencing problem, not a willpower one. Practical guidance on MS fatigue, brain fog, and cognitive load, plus a daily energy forecast from Myelina Health.";

const FAQS: { question: string; answer: string }[] = [
  {
    question: "What does living with MS actually look like day-to-day?",
    answer:
      "For most people with MS, the body's daily budget changes from morning to morning. The skill isn't pushing through, it's sequencing the day so the demanding things land inside your real energy windows, and the gentle things absorb the dips.",
  },
  {
    question: "Why is fatigue the symptom most people talk about?",
    answer:
      "Up to 80% of people with MS report fatigue as a primary symptom, and it's the one most likely to make you cancel a plan. Unlike ordinary tiredness, MS fatigue doesn't respond reliably to sleep, caffeine, or willpower, which is why predicting it matters more than tracking it.",
  },
  {
    question: "How do I explain invisible MS symptoms to family or work?",
    answer:
      "Concrete language beats apology. Instead of \"I'm tired,\" try \"My energy budget for today is small. I have about two demanding hours.\" A shared forecast (the Myelina Health Energy Map) turns it into a sentence anyone can plan around.",
  },
  {
    question: "Can I keep working full-time with MS?",
    answer:
      "Many do. The biggest predictors of sustainable work are heat management, predictable sleep, planned recovery windows, and a willingness to renegotiate the shape of the workday, not its hours. A daily forecast removes the guesswork from where to spend cognitive load.",
  },
  {
    question: "Where does community fit in?",
    answer:
      "Other women living with MS will believe you without explanation. Myelina Health has a Buddy system so you can share a daily one-line check-in with someone who gets it, privately, opt-in, never broadcast.",
  },
];

const LivingWithMs = () => {
  return (
    <>
      <PillarSeo
        slug="living-with-ms"
        title={META_TITLE}
        description={META_DESC}
        faqs={FAQS}
        date="2026-05-17"
        image="/og/pillar-living-with-ms.jpg"
        imageAlt="Living With MS. Sequencing, not willpower | Myelina Health"
      />
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="pt-32 pb-12 sm:pt-44 sm:pb-16">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <motion.div initial="hidden" animate="visible" variants={riseFade} className="max-w-3xl mx-auto text-center space-y-6">
              <div className="eyebrow justify-center mx-auto">For women living with MS</div>
              <h1 className="font-display text-foreground text-display-xl leading-[1.05]">
                Living with MS is a <span className="italic text-primary">sequencing</span> problem, not a willpower one.
              </h1>
              <p className="text-lg leading-[1.7] text-muted-foreground max-w-2xl mx-auto">
                The good days aren't louder, they're better arranged. Here's how to plan a day around real energy windows instead of fighting the body you have.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/demo">
                  <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                    See the demo
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/ms-fatigue-tracker">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    The fatigue tracker
                  </Button>
                </Link>
              </div>
              <ShareButtons slug="living-with-ms" title={META_TITLE} summary={META_DESC} className="justify-center mt-6" />
              <div className="flex justify-center mt-3">
                <BookmarkButton slug="living-with-ms" title="Living With MS" category="Pillar" kind="pillar" />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-5xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground text-center mb-12">
              Three shifts that change everything
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Compass, title: "Plan to your forecast, not the calendar", body: "A 9 a.m. meeting on a low-energy day is not the same meeting as one at 11. Move what you can; protect what you can't." },
                { icon: Heart, title: "Spend the budget, don't borrow against it", body: "Pushing through a dip means borrowing energy from tomorrow at a punitive rate. A planned rest costs less than an unplanned dip." },
                { icon: Users, title: "Make the invisible visible to one person", body: "A buddy who sees your one-line forecast can shape support without you having to re-explain. Shared context is the cheapest accommodation." },
              ].map((f) => (
                <Card key={f.title} className="p-6 sm:p-8 border-border/60 shadow-sm h-full">
                  <f.icon className="w-6 h-6 text-primary mb-3" />
                  <h3 className="font-display text-xl text-foreground mb-2">{f.title}</h3>
                  <p className="text-[15px] text-muted-foreground leading-relaxed">{f.body}</p>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-3xl mx-auto space-y-6">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground">
              The day as a budget, not a checklist
            </h2>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              Living with MS asks a different question than the productivity literature does. Not <em>how much can I do</em>, but <em>where in the day will the body let me do it</em>. The answer changes every 24 hours, which is why a one-time routine doesn't hold, and why a daily forecast does.
            </p>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              That's the Myelina Health bet: the same data your wearable already collects, plus a 60-second morning check-in, is enough to know, calmly, the night before, whether tomorrow is a build day or a maintain day. Then you plan accordingly, and stop apologizing for the body you have.
            </p>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              Myelina, the AI energy companion inside the app, turns that into one sentence. No charts to interpret at 7 a.m. when you're already tired.
            </p>
          </motion.div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24" aria-labelledby="pillar-living-faq">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-3xl mx-auto">
            <h2 id="pillar-living-faq" className="font-display text-3xl sm:text-4xl text-foreground text-center mb-10">Frequently asked questions</h2>
            <dl className="divide-y divide-border/60">
              {FAQS.map((faq) => (
                <div key={faq.question} className="py-5">
                  <dt className="font-display italic text-foreground text-xl mb-2">{faq.question}</dt>
                  <dd className="text-[15px] text-muted-foreground leading-relaxed">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </section>

        <RelatedPillars
          intro="Go deeper"
          links={[
            { to: "/ms-fatigue-tracker", label: "MS fatigue tracker" },
            { to: "/ms-energy-tracking", label: "MS energy tracking" },
          ]}
          trail="as a daily practice"
        />

        <RelatedArticles topics={["living with ms", "brain fog", "daily planning", "energy pacing"]} featuredSlugs={["brain-fog-ms-cognitive-fatigue", "spoon-theory-vs-energy-budgeting-ms", "best-ms-tracking-apps-2026"]} />
        <section className="bg-secondary py-20 sm:py-28">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
              The body you have today, <span className="italic text-primary">planned around.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              Three questions. No account. See what tomorrow could look like.
            </p>
            <Link to="/demo">
              <Button variant="premium" size="lg" className="group">
                See the demo
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default LivingWithMs;
